import { LoaderFunction, redirect } from "react-router";

import { orgStorage } from "@/shared/auth";
import { DatasetRecord } from "@/shared/domain/datasets";
import {
  QueryContentMode,
  QueryService,
  QueryTarget,
} from "@/shared/domain/query";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation/Goto";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

/**
 * Loads the datasets that match the given name and redirects
 * to either the single dataset match if there is only one match,
 * or the dataset search results page otherwise.
 */
export const loadDatasetByName: LoaderFunction = async ({
  params,
}): Promise<Response> => {
  try {
    const { datasetName } = params;
    const httpClient = await makeAuthenticatedHttpClient();

    const queryService = new QueryService(httpClient);
    const orgRecord = orgStorage.getCurrentOrg();

    // Redirect user to select orgs if org not found
    if (!orgRecord) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }
    const query = await queryService.submitRoboql(
      {
        target: QueryTarget.Datasets,
        query: `name = "${datasetName}"`,
        content_mode: QueryContentMode.RecordOnly,
      },
      { resourceOwnerId: orgRecord.org_id },
    );

    const resultCount = await queryService.getQueryResultCount(query.query_id);
    if (resultCount !== 1) {
      // If multiple datasets match the given name, go to the search results page.
      return redirect(`${Goto.route.Search}?queryId=${query.query_id}`);
    }

    const results = await queryService.getQueryResults(query.query_id);
    const datasetId = (results.items[0] as DatasetRecord).dataset_id;
    // If a single dataset resolves, go directly there.
    return redirect(Goto.createDatasetRoute(datasetId));
  } catch (error) {
    // Go to the full datasets list when an error occurs.
    ErrorMonitoringService.captureError(error);
    return redirect(Goto.route.Datasets);
  }
};
